/**
 * Directive for "disabled" attribute
 * @example
 * <template>
 *   <input :disabled={{inputDisabled}}/>
 * </template>
 *
 * <script>
 *     import { disabled } from 'sham-ui-directives';
 *
 *     function extendContext() {
 *         this.ctx.appendDirectives( { disabled } );
 *     }
 *
 *     function Input( options ) {
 *         options( {
 *             [ $.inputDisabled ]: true
 *         } );
 *     }
 *
 *     export default Component( extendContext, Template, Input );
 * </script>
 */
class disabled {
  constructor() {
    this.node = null;
  }

  bind(node) {
    this.node = node;
  }

  unbind() {
    this.node = null;
  }

  update(value) {
    this.node.disabled = value;
  }

}

/**
 * Base directive class for event listener
 * @example
 * import { EventListener } from 'sham-ui-directives';
 *
 * // directive for 'click' event listener
 * class onclick extends EventListener {
 *     constructor() {
 *         super( 'click' );
 *     }
 * }
 */
class EventListener {
  constructor(type) {
    this.type = type;
    this.handler = null;
    this.callback = this.callback.bind(this);
  }

  callback(event) {
    this.handler(event);
  }

  bind(node) {
    node.addEventListener(this.type, this.callback);
  }

  unbind(node) {
    node.removeEventListener(this.type, this.callback);
  }

  update(handler) {
    this.handler = handler;
  }

}
/**
 * Directive for 'click' listener
 * @example
 * <template>
 *   <button :onclick={{() => window.alert( 'Clicked' )}}>Click me!</button>
 * </template>
 * <script>
 *     import { onclick } from 'sham-ui-directives';
 *
 *     function extendContext() {
 *         this.ctx.appendDirectives( { onclick } );
 *     }
 *
 *     export default Component( extendContext, Template );
 * </script>
 */

class onclick extends EventListener {
  constructor() {
    super('click');
  }

}
/**
 * Directive for 'submit' listener
 */

class onsubmit extends EventListener {
  constructor() {
    super('submit');
  }

}
/**
 * Directive for 'load' listener
 */

class onload extends EventListener {
  constructor() {
    super('load');
  }

}
/**
 * Directive for 'resize' listener
 */

class onresize extends EventListener {
  constructor() {
    super('resize');
  }

}
/**
 * Directive for 'scroll' listener
 */

class onscroll extends EventListener {
  constructor() {
    super('scroll');
  }

}
/**
 * Directive for 'input' listener
 */

class oninput extends EventListener {
  constructor() {
    super('input');
  }

}
/**
 * Directive for 'select' listener
 */

class onselect extends EventListener {
  constructor() {
    super('select');
  }

}

/**
 * Directive for reference to Node
 * @example
 * <template>
 *   <input :ref={{$.inputField}} value="default value"/>
 * </template>
 * <script>
 *     import { ref } from 'sham-ui-directives';
 *
 *     function extendContext() {
 *         this.ctx.appendDirectives( { ref } );
 *     }
 *
 *     function Input( options, didMount ) {
 *         didMount( () => {
 *             alert( this.inputField.value );
 *         } )
 *     }
 *
 *     export default Component( extendContext, Template, Input );
 * </script>
 */
class ref {
  constructor(component) {
    this.component = component;
    this.node = null;
    this.name = null;
  }

  bind(node) {
    this.node = node;
  }

  unbind() {
    this.node = null;

    if (this.name) {
      delete this.component[this.name];
    }

    this.name = null;
  }

  update(name) {
    this.name = name;
    this.component[name] = this.node;
  }

}

/**
 * Base directive class for event listener
 * @example
 * import { UpdateOnEvent } from 'sham-ui-directives';
 *
 * // directive for update on 'input' event listener
 * class updateOnEvent extends UpdateOnEvent {
 *     constructor() {
 *         super( 'input' );
 *     }
 * }
 */
class UpdateOnEvent {
  constructor(component, type) {
    this.type = type;
    this.callback = this.callback.bind(this);
    this.component = component;
  }

  callback() {
    this.component.update({
      [this.field]: this._getValue()
    });
  }

  bind(node) {
    this.node = node;
    node.addEventListener(this.type, this.callback);
  }

  unbind(node) {
    node.removeEventListener(this.type, this.callback);
    this.node = null;
  }

  update(args) {
    this.field = args;
  }

  _getValue() {
    const node = this.node;

    if (node.type === 'checkbox') {
      return node.checked;
    }

    return node.value;
  }

}
/**
 * Update on 'change' event
 */

class onChangeUpdate extends UpdateOnEvent {
  constructor(component) {
    super(component, 'change');
  }

}
/**
 * Update on 'input' event
 */

class onInputUpdate extends UpdateOnEvent {
  constructor(component) {
    super(component, 'input');
  }

}

export { EventListener, UpdateOnEvent, disabled, onChangeUpdate, onInputUpdate, onclick, oninput, onload, onresize, onscroll, onselect, onsubmit, ref };
